'use strict';

// Pure engine orchestration (no DOM) — shared by window.Web3Keys (app.js) and the UI
// (ui/flows.js, app2.js). Kept framework-free so it also runs in the test harnesses.

const identity = require('./crypto/identity');
const api = require('./api');
const recovery = require('./crypto/recovery');
const { sealShare } = require('./crypto/seal');
const { resolveDepositKey } = require('./crypto/depositKey');
const { KDF, deriveKeyMaterial } = require('./crypto/kdf');
const { encryptVault } = require('./crypto/vault');
const { getBsv, randomBytes } = require('./crypto/lib');
const { toB64 } = require('./crypto/b64');

/**
 * Register: create the identity client-side, send only the registration payload, and set
 * up managed 2-of-3 recovery by splitting the MNEMONIC into U/C/S slices. The server stores
 * slice C and relays slice S, sealed to the recovery service's key, to that service; the mnemonic itself never leaves the
 * browser. Returns `created` (incl. `created.phrase` for the write-down gate and
 * `created.recoveryShareUser` = slice U, the user's recovery code).
 */
async function registerFlow(email, password, opts = {}) {
  const created = await identity.createIdentity(password, opts);
  try {
    const slices = recovery.splitMnemonic(created.phrase);
    created.recoveryShareUser = recovery.encodeShare(slices.user);  // U -> user keeps (code)
    created.registration.recoveryShareCompany = slices.company;     // C -> web3keys stores
    // S -> sealed to the service's key, so web3keys relays a ciphertext and never holds C and S
    // together in readable form. S is NEVER sent in plaintext: if sealing fails it is left out,
    // and the server records the account's recovery as missing slice S.
    try {
      created.registration.recoveryShareServiceSealed = await sealShare(slices.service, email, opts.depositKey || resolveDepositKey());
    } catch (e) {
      console.warn('[recovery] sealing slice S failed; S not deposited:', e && e.message);
    }
  } catch (e) {
    console.warn('[recovery] split failed (continuing without recovery):', e && e.message);
  }
  await api.register(created.registration, email);
  return created;
}

/**
 * Login from any device. Also returns { salt, kdf, vaultBlob } so the caller can
 * cache the encrypted vault for this device (devicevault.remember).
 */
async function loginFlow(email, password) {
  const { salt, kdf } = await api.params(email);
  const authVerifier = await identity.computeAuthVerifier(password, salt);
  const { vaultBlob, did, emailVerified } = await api.login(email, authVerifier);
  const opened = await identity.unlockIdentity(password, { salt, vaultBlob });
  return Object.assign({ did, emailVerified, salt, kdf, vaultBlob }, opened);
}

// --- recovery: the browser talks to the recovery service's PUBLIC endpoints directly ---
async function serviceRecoveryRequest(email) {
  const { shamirBaseUrl, companyId } = await api.recoveryConfig();
  if (!shamirBaseUrl) throw new Error('recovery service not configured');
  const r = await fetch(shamirBaseUrl.replace(/\/$/, '') + '/api/recovery/request', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, companyId }),
  });
  return r.json(); // { message, challengeId?, devCode? }
}
async function serviceRecoveryVerify(challengeId, code) {
  const { shamirBaseUrl } = await api.recoveryConfig();
  const r = await fetch(shamirBaseUrl.replace(/\/$/, '') + '/api/recovery/verify', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ challengeId, code }),
  });
  if (!r.ok) throw new Error('recovery verify failed');
  const d = await r.json();
  return d.shares && d.shares[0] && d.shares[0].share; // slice S (raw share object)
}

/**
 * Finalize recovery: reconstruct the mnemonic from two slices (service S + the user's U or
 * the released company C), rebuild the whole identity from it, re-encrypt the vault under a
 * NEW password, and persist. /reset is authorized by a signature from the RECOVERED identity
 * key — proving real-mnemonic possession (email control alone is never sufficient).
 * @param {object} o - { email, newPassword, serviceShare, secondShare, resetToken }
 */
async function recoverFlow({ email, newPassword, serviceShare, secondShare, nonce }) {
  const mnemonic = recovery.combineMnemonic(serviceShare, secondShare);
  const opened = await identity.identityFromMnemonic(mnemonic);
  const salt = randomBytes(16);
  const { authVerifier, vaultKeyBytes } = await deriveKeyMaterial(newPassword, salt);
  const vaultBlob = await encryptVault(vaultKeyBytes, { v: 2, mnemonic, did: opened.did, handle: opened.handle });
  const identitySig = getBsv().Message.sign('recover:' + nonce, opened.keys.identity.privateKey);
  await api.recoveryReset({ email, nonce, identitySig, salt: toB64(salt), kdf: KDF, authVerifier: toB64(authVerifier), vaultBlob });
  return Object.assign({ salt: toB64(salt), kdf: KDF, vaultBlob }, opened);
}

module.exports = { registerFlow, loginFlow, recoverFlow, serviceRecoveryRequest, serviceRecoveryVerify };
